import {
  IonButton,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
} from '@ionic/react'
import { Controller } from 'react-hook-form'
import { z } from 'zod'

import { cn } from '../../../utils/cn'
import { useZodForm } from '../../../utils/use-zod-form'
import { useUpdateClient } from '../../../hooks/clients/mutations'
import { ClientInfo } from '../../../hooks/clients/queries'
import { useFlatAccountGroups } from '../../../hooks/queries/use-groups'
import { useBoolean } from '../../../hooks/use-boolean'
import { Form } from '../../../components/ui/form/form'
import { InfoCard } from './info-card'

type Props = {
  clientId: string
  group: ClientInfo['group']
  isEditDisabled: boolean
  updateEditStatus: (status: boolean) => void
}

function AccountInfoCard({
  clientId,
  group,
  isEditDisabled,
  updateEditStatus,
}: Props) {
  const [isEditing, { off: hideEdit, on: showEdit }] = useBoolean()
  const groups = useFlatAccountGroups()
  const updateClient = useUpdateClient()

  const form = useZodForm({
    defaultValues: {
      groupId: group.objectId,
    },
    schema: z.object({ groupId: z.string().nullable() }),
  })

  return (
    <InfoCard>
      <Form
        form={form}
        onSubmit={(data) => {
          updateClient.mutate(
            { objectId: clientId, groupId: data.groupId },
            {
              onSuccess: () => {
                hideEdit()
                updateEditStatus(true)
              },
            }
          )
        }}
      >
        <div className="space-y-2">
          <IonItem
            style={{ '--padding-start': 0 }}
            className={cn(
              'rounded border pl-2',
              isEditing ? 'border-gray-400' : 'border-gray-400/40'
            )}
            color="secondary"
            lines="none"
          >
            <IonLabel>Store Location</IonLabel>
            <Controller
              name="groupId"
              render={({ field: { value, onChange } }) => (
                <IonSelect
                  disabled={!isEditing}
                  color="secondary"
                  value={value}
                  onIonChange={(e) => {
                    // HACK some reason revalidation causes reset to empty string
                    if (!e.detail.value) return
                    onChange(e.detail.value)
                  }}
                >
                  {groups.data?.map((group) => (
                    <IonSelectOption
                      key={group.objectId}
                      value={group.objectId}
                    >
                      {group.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              )}
            />
          </IonItem>
          <div>
            <div className={isEditing ? 'flex justify-between' : 'hidden'}>
              <IonButton
                fill="outline"
                size="small"
                onClick={() => {
                  hideEdit()
                  updateEditStatus(true)
                }}
                disabled={updateClient.isLoading}
              >
                Cancel
              </IonButton>
              <IonButton
                color="yellow"
                size="small"
                type="submit"
                // onClick={handleSubmit(handleSave)}
                disabled={updateClient.isLoading}
              >
                Save
              </IonButton>
            </div>
            <div className={isEditing ? 'hidden' : 'flex justify-end'}>
              <IonButton
                size="small"
                fill="outline"
                disabled={isEditDisabled}
                onClick={() => {
                  showEdit()
                  updateEditStatus(false)
                }}
              >
                Edit
              </IonButton>
            </div>
          </div>
        </div>
      </Form>
    </InfoCard>
  )
}

export { AccountInfoCard }
